import { useFhirData } from "@/utils/useFhirData";
import { documentBelongsToEncounter } from "./editHydrationHelpers";
import { enrichMedicationsFromCmed } from "./enrichMedicationInterventionFromCmed";
import {
  collectEncounterDocuments,
  enrichInterventionsWithSignedDocuments,
} from "./enrichInterventionsWithSignedDocuments";
import { mapCertificateDocumentToIntervention } from "./mapCertificateAndProtocol";
import { mapMedicationRequestToIntervention } from "./mapMedicationRequestToIntervention";
import { mapServiceRequestToIntervention } from "./mapServiceRequestToIntervention";

/**
 * Coleta intervenções do encounter para pré-preencher a edição.
 * 1) Mapeia MR / SR / atestado
 * 2) Enriquece medicações via CMED (GetMedication por code) — async
 * 3) Vincula DocumentReference (assinado ou pendente) via basedOn;
 *    órfãos (MR sem doc) ficam !sign para o finish postar DocumentReference
 */
export async function collectEncounterInterventions({
  summaryData,
  encounterId,
}: {
  summaryData: any;
  encounterId: string;
}) {
  const medicationBundle = useFhirData.extractPatientSummaryResource(
    summaryData,
    "MedicationRequest"
  )?.[0];

  const serviceBundle = useFhirData.extractPatientSummaryResource(
    summaryData,
    "ServiceRequest"
  )?.[0];

  const certificateBundle = useFhirData.extractPatientSummaryResource(
    summaryData,
    "DocumentReference",
    "atestado"
  )?.[0];

  const medicationsMapped =
    medicationBundle?.entry
      ?.map((e: any) => e?.resource)
      ?.filter(
        (el: any) =>
          el?.resourceType === "MedicationRequest" &&
          el?.encounter?.reference?.includes(encounterId)
      )
      ?.map(mapMedicationRequestToIntervention) || [];

  // CMED em paralelo; não bloqueia o shape básico se alguma busca falhar
  const medications = await enrichMedicationsFromCmed(medicationsMapped);

  const servicesAndProcedures =
    serviceBundle?.entry
      ?.map((e: any) => e?.resource)
      ?.filter(
        (el: any) =>
          el?.resourceType === "ServiceRequest" &&
          el?.encounter?.reference?.includes(encounterId)
      )
      ?.map(mapServiceRequestToIntervention) || [];

  const certificates =
    certificateBundle?.entry
      ?.map((e: any) => e?.resource)
      ?.filter(
        (el: any) =>
          el?.resourceType === "DocumentReference" &&
          documentBelongsToEncounter(el, encounterId)
      )
      ?.map(mapCertificateDocumentToIntervention) || [];

  const encounterDocuments = collectEncounterDocuments(
    summaryData,
    encounterId
  );

  const interventions = enrichInterventionsWithSignedDocuments(
    [...medications, ...servicesAndProcedures, ...certificates],
    encounterDocuments
  );

  const signedDocuments = encounterDocuments.filter((d) => d.signed);

  return { interventions, signedDocuments };
}
